import React, { useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import Chip from '@material-ui/core/Chip';

import { EmployeeUserSelector } from './employee-user-selector';
import { loadOrgUsers } from '../action/org/load-org-users';
import { usersForOrgIdSelector } from '../reducers/user-selectors';

import { UiButton } from '../ui/ui-button';

type InputProps = {
  label?: string;
  orgId: number;
  value: Array<{ username: string }>;
  onChange: (usernames: Array<{ username: string }>) => void;
  className?: string;
  children?: React.ReactNode;
};

const mapStateToProps = (state: any, { orgId }: InputProps) => ({
  users: usersForOrgIdSelector(state, orgId),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId }: InputProps,
) => ({
  actions: {
    loadUsers: () => dispatch(loadOrgUsers(orgId)),
  },
});

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

export const MultipleEmployeeUsersSelector = connect(
  mapStateToProps,
  mapDispatchToProps,
)((props: Props) => {
  const { users, value, onChange, actions, orgId, className, label, children } = props;
  const [selectorIsVisible, setSelectorIsVisible] = useState<boolean>(false);

  useEffect(() => {
    actions.loadUsers();
  });

  if (!users) {
    return null;
  }

  const handleAddClick = () => {
    setSelectorIsVisible(true);
  };

  const handleCancelClick = () => setSelectorIsVisible(false);

  const handleRecipientSelect = (username: string | null) => {
    if (username) {
      onChange(value.concat([{ username }]));
    }
    setSelectorIsVisible(false);
  };

  const handleRemoveRecipientClick = (username: string) => () => {
    onChange(value.filter((u) => u.username !== username));
  };

  return (
    <div
      className={`u-borderRadius-3 u-position-relative ${className}`}
      style={{ border: '1px solid #c4c4c4' }}
    >
      <div
        style={{ top: -6, left: 10 }}
        className="u-padding-x-xs u-background-white u-lineHeight-1 typo-subtitle u-position-absolute"
      >
        {label}
      </div>
      <div className="u-flex-center u-flexWrap-wrap u-padding">
        {value.map((v) => {
          const user = users.find((u) => u.username === v.username);
          return user?.username ? (
            <Chip
              className="u-margin-right-sm"
              key={user.username}
              color="primary"
              size="small"
              label={`${user.firstName} ${user.lastName}`}
              onDelete={handleRemoveRecipientClick(user.username)}
            />
          ) : null;
        })}
        {selectorIsVisible ? (
          <div className="u-spread u-minWidth-350px">
            <div className="u-width-100">
              <EmployeeUserSelector
                autoFocus
                orgId={orgId}
                value={null}
                onChange={handleRecipientSelect}
                excludeUsernames={value.map((u) => u.username)}
              />
            </div>
            <UiButton variant="dismiss" onClick={handleCancelClick}>
              Cancel
            </UiButton>
          </div>
        ) : (
          <UiButton
            size="xxs"
            variant="tertiary"
            onClick={handleAddClick}
            className="u-margin-y-sm"
          >
            Add Recipient
          </UiButton>
        )}
      </div>
      {children}
    </div>
  );
});
